/**
 * Clamp card/block in+out so they fit inside a fixed content budget.
 * When both are positive and sum exceeds budget, scale both by `budget / (in+out)`.
 * When one is 0, clamp the other to the budget.
 */
export function clampInOutToBudget(
	inMs: number,
	outMs: number,
	budgetMs: number,
): { inMs: number; outMs: number } {
	const budget = Math.max(0, Math.round(budgetMs));
	let nextIn = Math.max(0, Math.round(inMs));
	let nextOut = Math.max(0, Math.round(outMs));

	if (nextIn + nextOut <= budget) {
		return { inMs: nextIn, outMs: nextOut };
	}
	if (nextIn === 0 && nextOut === 0) {
		return { inMs: 0, outMs: 0 };
	}
	if (nextIn === 0) {
		return { inMs: 0, outMs: Math.min(nextOut, budget) };
	}
	if (nextOut === 0) {
		return { inMs: Math.min(nextIn, budget), outMs: 0 };
	}

	const scale = budget / (nextIn + nextOut);
	nextIn = Math.round(nextIn * scale);
	nextOut = Math.round(nextOut * scale);

	let overflow = nextIn + nextOut - budget;
	if (overflow > 0) {
		if (nextOut >= overflow) {
			nextOut -= overflow;
		} else {
			overflow -= nextOut;
			nextOut = 0;
			nextIn = Math.max(0, nextIn - overflow);
		}
	}

	return { inMs: nextIn, outMs: nextOut };
}

export interface SegmentBlockMotionInput {
	blockId: string;
	inMs: number;
	outMs: number;
}

export interface SegmentBlockCueWindow {
	blockId: string;
	/** Relative to segment start (content timeline). */
	startMs: number;
	endMs: number;
}

export interface SegmentBlockMotionSchedule {
	blockId: string;
	/** Cue / slice bounds (fixed when animations grow — hold shrinks). */
	cueStartMs: number;
	cueEndMs: number;
	inMs: number;
	holdMs: number;
	outMs: number;
	/** Absolute start of animate-in within the segment. */
	fromMs: number;
}

export interface ResolveSegmentMotionScheduleInput {
	/** Immutable content duration (no trailing silence). */
	contentMs: number;
	cardTransitionInMs: number;
	cardTransitionOutMs: number;
	blocks: readonly SegmentBlockMotionInput[];
	/** Optional narration cue windows; when absent/empty → equal-split fallback. */
	blockCueWindows?: readonly SegmentBlockCueWindow[] | null;
}

export interface SegmentMotionSchedule {
	contentMs: number;
	cardInMs: number;
	cardOutMs: number;
	/** Hold window start (= cardInMs). */
	contentStartMs: number;
	/** Hold window end (= contentMs - cardOutMs). */
	contentEndMs: number;
	blocks: SegmentBlockMotionSchedule[];
}

function scheduleBlockInsideWindow(input: {
	blockId: string;
	cueStartMs: number;
	cueEndMs: number;
	inMs: number;
	outMs: number;
}): SegmentBlockMotionSchedule {
	const cueStartMs = Math.max(0, Math.round(input.cueStartMs));
	const cueEndMs = Math.max(cueStartMs, Math.round(input.cueEndMs));
	const windowMs = cueEndMs - cueStartMs;
	const clamped = clampInOutToBudget(input.inMs, input.outMs, windowMs);
	const holdMs = Math.max(0, windowMs - clamped.inMs - clamped.outMs);
	return {
		blockId: input.blockId,
		cueStartMs,
		cueEndMs,
		inMs: clamped.inMs,
		holdMs,
		outMs: clamped.outMs,
		fromMs: cueStartMs,
	};
}

/**
 * Schedule card + block motion **inside** `contentMs` (no additive motion).
 * Timeline span after scheduling equals `contentMs` (0 ms tolerance at ms layer).
 */
export function resolveSegmentMotionSchedule(
	input: ResolveSegmentMotionScheduleInput,
): SegmentMotionSchedule {
	const contentMs = Math.max(0, Math.round(input.contentMs));
	const card = clampInOutToBudget(
		input.cardTransitionInMs,
		input.cardTransitionOutMs,
		contentMs,
	);
	const contentStartMs = card.inMs;
	const contentEndMs = contentMs - card.outMs;
	const holdWindowMs = Math.max(0, contentEndMs - contentStartMs);

	const blocks = input.blocks ?? [];
	if (blocks.length === 0) {
		return {
			contentMs,
			cardInMs: card.inMs,
			cardOutMs: card.outMs,
			contentStartMs,
			contentEndMs,
			blocks: [],
		};
	}

	const cueById = new Map<string, SegmentBlockCueWindow>();
	for (const cue of input.blockCueWindows ?? []) {
		cueById.set(cue.blockId, cue);
	}
	const useCues = blocks.every((block) => cueById.has(block.blockId));

	const scheduled: SegmentBlockMotionSchedule[] = [];

	if (useCues) {
		for (const block of blocks) {
			const cue = cueById.get(block.blockId);
			if (!cue) {
				continue;
			}
			const rawStart = Math.max(contentStartMs, Math.round(cue.startMs));
			const rawEnd = Math.min(contentEndMs, Math.round(cue.endMs));
			const cueStartMs = Math.min(rawStart, contentEndMs);
			const cueEndMs = Math.max(
				cueStartMs,
				Math.min(rawEnd, contentEndMs),
			);
			scheduled.push(
				scheduleBlockInsideWindow({
					blockId: block.blockId,
					cueStartMs,
					cueEndMs,
					inMs: block.inMs,
					outMs: block.outMs,
				}),
			);
		}
	} else {
		const sliceMs =
			blocks.length > 0 ? Math.floor(holdWindowMs / blocks.length) : 0;
		let cursor = contentStartMs;
		blocks.forEach((block, index) => {
			const isLast = index === blocks.length - 1;
			const cueStartMs = cursor;
			const cueEndMs = isLast
				? contentEndMs
				: Math.min(contentEndMs, cursor + sliceMs);
			scheduled.push(
				scheduleBlockInsideWindow({
					blockId: block.blockId,
					cueStartMs,
					cueEndMs,
					inMs: block.inMs,
					outMs: block.outMs,
				}),
			);
			cursor = cueEndMs;
		});
	}

	return {
		contentMs,
		cardInMs: card.inMs,
		cardOutMs: card.outMs,
		contentStartMs,
		contentEndMs,
		blocks: scheduled,
	};
}

export interface NarrationCue {
	startMs: number;
	endMs: number;
}

/**
 * Narration cues → one window per block: sequential cue groups map to block order, clamped into
 * `[0, segmentMs]`. No usable cues → `[]`, and the schedule falls back to an equal split.
 */
export function groupCuesIntoBlockWindows(input: {
	cues: readonly NarrationCue[] | null | undefined;
	/** Ordered block ids. */
	blockIds: readonly string[];
	segmentMs: number;
}): SegmentBlockCueWindow[] {
	const blockIds = input.blockIds.filter(
		(id) => typeof id === "string" && id.length > 0,
	);
	const cues = (input.cues ?? []).filter(
		(cue) =>
			Number.isFinite(cue.startMs) &&
			Number.isFinite(cue.endMs) &&
			cue.endMs > cue.startMs,
	);
	if (blockIds.length === 0 || cues.length === 0) {
		return [];
	}
	const segmentMs = Math.max(0, Math.round(input.segmentMs));
	const windows: SegmentBlockCueWindow[] = [];
	blockIds.forEach((blockId, groupIndex) => {
		const groupCues = cues.slice(
			Math.floor((groupIndex * cues.length) / blockIds.length),
			Math.floor(((groupIndex + 1) * cues.length) / blockIds.length),
		);
		const first = groupCues[0];
		const last = groupCues[groupCues.length - 1];
		if (!first || !last) {
			return;
		}
		const startMs = Math.max(
			0,
			Math.min(segmentMs, Math.round(first.startMs)),
		);
		windows.push({
			blockId,
			startMs,
			endMs: Math.max(
				startMs,
				Math.min(segmentMs, Math.round(last.endMs)),
			),
		});
	});
	return windows;
}
